import { useState, useMemo } from "react";
import {
  Calendar,
  Award,
  Users,
  Cake,
  Star,
  PartyPopper,
  Trophy,
  UserPlus,
} from "lucide-react";

const EmployeeCelebrationCard = ({ celebrationData }) => {
  const [activeTab, setActiveTab] = useState("birthdays");

  const formatDateShort = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
    });
  };

  const isToday = (dateString) => {
    if (!dateString) return false;
    const today = new Date();
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return false;
    return (
      today.getMonth() === date.getMonth() && today.getDate() === date.getDate()
    );
  };

  const isWorkAnniversaryToday = (dojString) => {
    if (!dojString) return false;
    const today = new Date();
    const doj = new Date(dojString);
    if (isNaN(doj.getTime())) return false;
    const yearsCompleted = today.getFullYear() - doj.getFullYear();
    if (yearsCompleted < 1) return false;
    return (
      today.getMonth() === doj.getMonth() && today.getDate() === doj.getDate()
    );
  };

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const upcomingBirthdays = useMemo(() => {
    return (
      celebrationData?.birthdays?.filter((person) => {
        if (!person.dob) return false;
        const dob = new Date(person.dob);
        if (isNaN(dob.getTime())) return false;
        const personBirthdayThisYear = new Date(
          today.getFullYear(),
          dob.getMonth(),
          dob.getDate()
        );
        personBirthdayThisYear.setHours(0, 0, 0, 0);
        return personBirthdayThisYear >= today;
      }) || []
    );
  }, [celebrationData?.birthdays, today]);

  const upcomingAnniversaries = useMemo(() => {
    return (
      celebrationData?.workAnniversaries?.filter((person) => {
        if (!person.doj) return false;
        const doj = new Date(person.doj);
        if (isNaN(doj.getTime())) return false;
        if (
          today.getFullYear() - doj.getFullYear() < 1 &&
          !(
            today.getMonth() === doj.getMonth() &&
            today.getDate() === doj.getDate()
          )
        ) {
          return false;
        }
        const personAnniversaryThisYear = new Date(
          today.getFullYear(),
          doj.getMonth(),
          doj.getDate()
        );
        personAnniversaryThisYear.setHours(0, 0, 0, 0);
        return personAnniversaryThisYear >= today;
      }) || []
    );
  }, [celebrationData?.workAnniversaries, today]);

  const upcomingNewJoiners = useMemo(() => {
    return (
      celebrationData?.newJoiners?.filter((person) => {
        if (!person.doj) return false;
        const joinDate = new Date(person.doj);
        joinDate.setHours(0, 0, 0, 0);
        return joinDate >= today;
      }) || []
    );
  }, [celebrationData?.newJoiners, today]);

  const todayCounts = useMemo(() => {
    const todayBirthdays =
      upcomingBirthdays.filter((person) => isToday(person.dob))?.length || 0;
    const todayAnniversaries =
      upcomingAnniversaries.filter((person) =>
        isWorkAnniversaryToday(person.doj)
      )?.length || 0;
    const todayNewJoiners =
      upcomingNewJoiners.filter((person) => isToday(person.doj))?.length || 0;

    return {
      birthdays: todayBirthdays,
      anniversaries: todayAnniversaries,
      newJoiners: todayNewJoiners,
    };
  }, [upcomingBirthdays, upcomingAnniversaries, upcomingNewJoiners]);

  const tabs = [
    {
      id: "birthdays",
      label: "Birthdays",
      icon: Calendar,
      count: upcomingBirthdays.length,
      todayCount: todayCounts.birthdays,
    },
    {
      id: "anniversaries",
      label: "Work Anniversary",
      icon: Award,
      count: upcomingAnniversaries.length,
      todayCount: todayCounts.anniversaries,
    },
    {
      id: "newJoiners",
      label: "New Joiners",
      icon: Users,
      count: upcomingNewJoiners.length,
      todayCount: todayCounts.newJoiners,
    },
  ];

  const renderTodayCard = (person, type) => {
    const bgColor = "bg-gradient-to-br from-yellow-50 to-amber-50";
    const borderColor = "border-yellow-300 border-2";
    const textColor = "text-amber-800";
    const iconColor = "text-yellow-600";

    const getBadgeIcon = () => {
      switch (type) {
        case "birthdays":
          return <PartyPopper className="w-3 h-3 inline mr-1" />;
        case "anniversaries":
          return <Trophy className="w-3 h-3 inline mr-1" />;
        case "newJoiners":
          return <UserPlus className="w-3 h-3 inline mr-1" />;
        default:
          return <PartyPopper className="w-3 h-3 inline mr-1" />;
      }
    };

    return (
      <div
        key={person.empId}
        className={`relative flex flex-col items-center text-center group p-3 rounded-xl ${bgColor} ${borderColor} border transition-all duration-300 hover:shadow-md`}
      >
        <div className="absolute -top-2 -right-2 z-10">
          <div className="bg-gradient-to-r from-yellow-400 to-orange-400 text-white text-xs px-2 py-1 rounded-full shadow-lg animate-pulse">
            {getBadgeIcon()}
            Today!
          </div>
        </div>
        <div
          className={`w-12 h-12 rounded-full border-2 ${borderColor} flex items-center justify-center ${iconColor} font-medium text-sm mb-2 group-hover:scale-105 transition-transform overflow-hidden shadow-md`}
        >
          {person.profileImage ? (
            <img
              src={`data:image/jpeg;base64,${person.profileImage}`}
              alt={`${person.firstName} ${person.lastName}`}
              className="w-full h-full object-cover rounded-full"
            />
          ) : (
            <>
              {person.firstName.charAt(0)}
              {person.lastName ? person.lastName.charAt(0) : ""}
            </>
          )}
        </div>
        <h3 className={`text-sm font-bold mb-1 leading-tight ${textColor}`}>
          {person.firstName} {person.lastName}
        </h3>
        <div className="flex flex-col items-center space-y-1">
          <p className={`text-xs ${textColor}`}>
            {formatDateShort(type === "birthdays" ? person.dob : person.doj)}
          </p>
          {type === "anniversaries" && person.yearsCompleted && (
            <div className="text-xs px-2 py-0.5 rounded-full bg-yellow-200 text-yellow-800">
              {person.yearsCompleted}{" "}
              {person.yearsCompleted === 1 ? "year" : "years"}
            </div>
          )}
          {type === "newJoiners" && (
            <div className="text-xs px-2 py-0.5 rounded-full bg-yellow-200 text-yellow-800">
              Welcome!
            </div>
          )}
        </div>
        <div className="absolute inset-0 pointer-events-none">
          <Star className="w-4 h-4 text-yellow-400 absolute top-1 left-1 animate-pulse" />
          <Star
            className="w-3 h-3 text-yellow-300 absolute bottom-2 right-2 animate-pulse"
            style={{ animationDelay: "0.5s" }}
          />
        </div>
      </div>
    );
  };

  const renderSimpleCard = (person, type) => {
    const getColorClass = () => {
      switch (type) {
        case "birthdays":
          return "blue";
        case "anniversaries":
          return "green";
        case "newJoiners":
          return "orange";
        default:
          return "gray";
      }
    };
    const colorClass = getColorClass();

    return (
      <div
        key={person.empId}
        className="flex flex-col items-center text-center group"
      >
        <div
          className={`w-10 h-10 rounded-full bg-${colorClass}-50 border-2 border-${colorClass}-100 flex items-center justify-center text-${colorClass}-600 font-medium text-sm mb-2 group-hover:bg-${colorClass}-100 transition-colors overflow-hidden`}
        >
          {person.profileImage ? (
            <img
              src={`data:image/jpeg;base64,${person.profileImage}`}
              alt={`${person.firstName} ${person.lastName}`}
              className="w-full h-full object-cover rounded-full"
            />
          ) : (
            <>
              {person.firstName.charAt(0)}
              {person.lastName ? person.lastName.charAt(0) : ""}
            </>
          )}
        </div>
        <h3 className="text-xs font-medium text-gray-800 mb-1 leading-tight">
          {person.firstName} {person.lastName}
        </h3>
        <p className="text-xs text-gray-400">
          {formatDateShort(type === "birthdays" ? person.dob : person.doj)}
        </p>
        {type === "anniversaries" && person.yearsCompleted && (
          <div className="text-xs px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600 mt-1">
            {person.yearsCompleted}y
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case "birthdays": {
        if (upcomingBirthdays.length === 0) {
          return (
            <div className="flex flex-col items-center justify-center  text-gray-500">
              <div className="w-16 h-16 bg-blue-50 text-blue-500 rounded-full flex items-center justify-center shadow-sm mb-4">
                <Cake className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                No Upcoming Birthdays
              </h3>
              <p className="text-sm text-gray-500 text-center max-w-xs">
                There are no more birthdays scheduled this year.
              </p>
            </div>
          );
        }
        const sortedBirthdays = [...upcomingBirthdays].sort((a, b) => {
          const a_bday = new Date(
            today.getFullYear(),
            new Date(a.dob).getMonth(),
            new Date(a.dob).getDate()
          );
          const b_bday = new Date(
            today.getFullYear(),
            new Date(b.dob).getMonth(),
            new Date(b.dob).getDate()
          );
          return a_bday - b_bday;
        });
        const todaysBirthdays = sortedBirthdays.filter((p) => isToday(p.dob));
        const otherBirthdays = sortedBirthdays.filter((p) => !isToday(p.dob));
        return (
          <div className="mt-0 mx-2 space-y-4">
            {todaysBirthdays.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700  flex items-center">
                  <PartyPopper className="w-4 h-4 mr-2 text-yellow-500" />
                  Celebrating Today
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {todaysBirthdays.map((p) => renderTodayCard(p, "birthdays"))}
                </div>
              </div>
            )}
            {otherBirthdays.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700  flex items-center">
                  <Calendar className="w-4 h-4 mr-2 text-blue-500" />
                  Upcoming Birthdays
                </h4>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                  {otherBirthdays.map((p) => renderSimpleCard(p, "birthdays"))}
                </div>
              </div>
            )}
          </div>
        );
      }
      case "anniversaries": {
        if (upcomingAnniversaries.length === 0) {
          return (
            <div className="flex flex-col items-center justify-center text-gray-500">
              <div className="w-16 h-16 bg-green-50 text-green-500 rounded-full flex items-center justify-center shadow-sm mb-4">
                <Trophy className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                No Upcoming Anniversaries
              </h3>
              <p className="text-sm text-gray-500 text-center max-w-xs">
                There are no more anniversaries scheduled this year.
              </p>
            </div>
          );
        }
        const sortedAnniversaries = [...upcomingAnniversaries].sort((a, b) => {
          const a_anniversary = new Date(
            today.getFullYear(),
            new Date(a.doj).getMonth(),
            new Date(a.doj).getDate()
          );
          const b_anniversary = new Date(
            today.getFullYear(),
            new Date(b.doj).getMonth(),
            new Date(b.doj).getDate()
          );
          return a_anniversary - b_anniversary;
        });
        const todaysAnniversaries = sortedAnniversaries.filter((p) =>
          isWorkAnniversaryToday(p.doj)
        );
        const otherAnniversaries = sortedAnniversaries.filter(
          (p) => !isWorkAnniversaryToday(p.doj)
        );
        return (
          <div className="space-y-4 mt-0 mx-2">
            {todaysAnniversaries.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 flex items-center">
                  <Trophy className="w-4 h-4 mr-2 text-yellow-500" />
                  Celebrating Today
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-2">
                  {todaysAnniversaries.map((p) =>
                    renderTodayCard(p, "anniversaries")
                  )}
                </div>
              </div>
            )}
            {otherAnniversaries.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 flex items-center">
                  <Award className="w-4 h-4 mr-2 text-green-500" />
                  Upcoming Anniversaries
                </h4>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                  {otherAnniversaries.map((p) =>
                    renderSimpleCard(p, "anniversaries")
                  )}
                </div>
              </div>
            )}
          </div>
        );
      }
      case "newJoiners": {
        if (upcomingNewJoiners.length === 0) {
          return (
            <div className="flex flex-col items-center justify-center text-gray-500">
              <div className="w-16 h-16 bg-orange-50 text-orange-500 rounded-full flex items-center justify-center shadow-sm mb-4">
                <Users className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                No Upcoming Joiners
              </h3>
              <p className="text-sm text-gray-500 text-center max-w-xs">
                There are no new joiners scheduled for today or in the future.
              </p>
            </div>
          );
        }
        const sortedNewJoiners = [...upcomingNewJoiners].sort(
          (a, b) => new Date(a.doj) - new Date(b.doj)
        );
        const todaysNewJoiners = sortedNewJoiners.filter((p) => isToday(p.doj));
        const otherNewJoiners = sortedNewJoiners.filter((p) => !isToday(p.doj));
        return (
          <div className="space-y-4 mt-0 mx-2">
            {todaysNewJoiners.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 flex items-center">
                  <UserPlus className="w-4 h-4 mr-2 text-yellow-500" />
                  Joining Today
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-2">
                  {todaysNewJoiners.map((p) =>
                    renderTodayCard(p, "newJoiners")
                  )}
                </div>
              </div>
            )}
            {otherNewJoiners.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 flex items-center">
                  <Calendar className="w-4 h-4 mr-2 text-orange-500" />
                  Upcoming Joiners
                </h4>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                  {otherNewJoiners.map((p) =>
                    renderSimpleCard(p, "newJoiners")
                  )}
                </div>
              </div>
            )}
          </div>
        );
      }
      default:
        return (
          <div className="flex items-center justify-center h-full text-gray-500">
            <p className="text-sm">No Data</p>
          </div>
        );
    }
  };

  const getTabBadgeIcon = (tabId) => {
    switch (tabId) {
      case "birthdays":
        return <PartyPopper className="w-2 h-2" />;
      case "anniversaries":
        return <Trophy className="w-2 h-2" />;
      case "newJoiners":
        return <UserPlus className="w-2 h-2" />;
      default:
        return <Award className="w-2 h-2" />;
    }
  };

  return (
    <div
      className="w-full h-full rounded-2xl shadow-sm border border-gray-100 relative overflow-hidden"
      style={{
        backgroundColor: "var(--bg-primary, #ffffff)",
        color: "var(--text-primary, #000000)",
      }}
    >
      <div className="p-2 border-b border-gray-100">
        <div className="flex space-x-1 bg-gray-100 p-0.5 rounded-full">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center p-2 rounded-full text-xs font-semibold transition-all relative ${
                  activeTab === tab.id
                    ? "bg-white shadow-sm text-gray-900"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <Icon className="w-3 h-3 mr-1" />
                <span>{tab.label}</span>
                {tab.todayCount > 0 && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-yellow-400 to-orange-400 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
                    {getTabBadgeIcon(tab.id)}
                  </div>
                )}
                {tab.count > 0 && (
                  <span
                    className={`ml-1 px-1 py-0.5 text-xs rounded-full font-semibold ${
                      tab.todayCount > 0
                        ? "bg-yellow-200 text-yellow-800"
                        : "bg-gray-200 text-gray-600"
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
      <div className="p-3 overflow-y-auto h-max">{renderContent()}</div>
    </div>
  );
};

export default EmployeeCelebrationCard;
